/**
 * Bootstrap + event wiring. Framework-free by decision (build map, locked
 * 2026-08-10): one render function over a store, delegated clicks, no VDOM.
 */

import './styles.css';
import { BoardApi } from './api';
import { BoardStore } from './store';
import { renderBoard } from './render/board';
import { mountArc } from './render/arc';
import { computeArc, msUntilNextArcEvent, type ArcState } from './day-arc';
import { applyDevClockParam, now } from './clock';
import { nextEvent } from './spotlight';
import type { ComposeItemInput } from './types';

const root = document.getElementById('root') as HTMLElement;

/** The dev mock honours a `scenario` param; in production it is simply unused. */
const params = new URLSearchParams(location.search);
const extras: Record<string, string> = {};
for (const k of ['scenario', 'fail']) {
  const v = params.get(k);
  if (v) extras[k] = v;
}

// Dev-only wall-clock shift (`?at=22:30`) so every day-arc face is reachable
// without waiting for 22:00. `now()` is plain `new Date()` in production.
if (import.meta.env.DEV) applyDevClockParam(location.search);

const store = new BoardStore(new BoardApi('/api', extras));

/**
 * Phone cut (surfaces ticket 04): Spotlight + check-offs + Mode toggle, and no
 * compose — composing is a sit-down act. A viewport of 0 means the page has not
 * been laid out yet (a kiosk tab waking up); assume the sit-down surface rather
 * than silently hiding the compose.
 */
const PHONE_MAX_PX = 720;
const canCompose = (): boolean => {
  const w = window.innerWidth || document.documentElement.clientWidth || 0;
  return w === 0 || w > PHONE_MAX_PX;
};

/* --------------------------------------------------------------- day arc */
/*
 * The board's faces across the day (product ticket 04, CONTEXT.md "Invite
 * state"). The logic is in `day-arc.ts`; here it is only wired: the resting
 * faces live in their own overlay so their fades survive a re-render, and the
 * board itself simply fades under them.
 */

const arc = mountArc();
/** Epoch ms (board clock) of the last interaction — the reveal's anchor. */
let lastWakeAt: number | null = null;
let arcTimer: number | null = null;

function arcNow(at: Date): ArcState {
  return computeArc({
    now: at,
    lastWakeAt,
    modeOn: store.state?.data.state.data?.mode.isOn ?? false,
    composing: store.ui.composing,
  });
}

/** Fade back the moment the reveal lapses or the hour turns, not 20s later. */
function scheduleArcTick(at: Date): void {
  if (arcTimer !== null) clearTimeout(arcTimer);
  const delay = msUntilNextArcEvent({
    now: at,
    lastWakeAt,
    modeOn: store.state?.data.state.data?.mode.isOn ?? false,
    composing: store.ui.composing,
  });
  arcTimer = setTimeout(scheduleRender, delay) as unknown as number;
}

let renderScheduled = false;
function render(): void {
  const at = now();
  const state = arcNow(at);

  // While a resting face is up the board underneath is invisible; don't pay
  // to re-render it (a kiosk sits here for three hours a night).
  if (state.face === 'board') {
    const focus = captureFocus();
    root.innerHTML = renderBoard({
      state: store.state,
      ui: store.ui,
      now: at,
      canCompose: canCompose(),
    });
    restoreFocus(focus);
  }
  root.classList.toggle('resting', state.face !== 'board');

  const board = store.state?.data;
  arc.update(
    state,
    board
      ? {
          directions: board.defs.data.directions,
          next: nextEvent(board.calendar.data.events ?? [], at),
          now: at,
        }
      : null,
  );

  scheduleArcTick(at);
  renderScheduled = false;
}

/**
 * A resting face is a resting face, not a lockout: one click, tap, key or
 * scroll brings the whole board — Spotlight included — back for
 * `ARC.revealMs`, then it settles again. The listeners are passive and
 * capture-phase, so the interaction that wakes the board is spent on waking
 * it and does not also tick something the sleeper could not see.
 */
function wake(ev: Event): void {
  const t = ev.target as HTMLElement | null;
  if (t?.closest?.('.dev-bar')) return; // dev chrome is not an interaction
  const wasResting = arcNow(now()).face !== 'board';
  lastWakeAt = now().getTime();
  if (!wasResting) return;
  // The poll is tapered (22:00) or stopped (01:00) at these hours by
  // decision; a board that just came back should not be showing 01:00 data.
  void store.refresh(false);
  scheduleRender();
}

for (const type of ['pointerdown', 'keydown', 'wheel', 'touchstart']) {
  document.addEventListener(type, wake, { passive: true, capture: true });
}

function scheduleRender(): void {
  if (renderScheduled) return;
  renderScheduled = true;
  // A microtask, not requestAnimationFrame: the kiosk browser may be on an
  // idle/backgrounded display, where rAF stops firing and the board would
  // silently stop updating.
  queueMicrotask(render);
}

store.subscribe(scheduleRender);
addEventListener('resize', scheduleRender);

/* ------------------------------------------------------------- interaction */

root.addEventListener('click', (ev) => {
  const target = ev.target as HTMLElement;

  const check = target.closest<HTMLElement>('[data-check]');
  if (check) {
    const id = check.dataset.id as string;
    const done = check.dataset.done !== '1';
    switch (check.dataset.check) {
      case 'habit':
        store.toggleHabit(id, done);
        break;
      case 'planItem':
        store.togglePlanItem(id, done);
        break;
      case 'reminder':
        store.toggleReminder(id, done);
        break;
    }
    return;
  }

  const fold = target.closest<HTMLElement>('[data-fold]');
  if (fold) {
    store.toggleFold(fold.dataset.fold as string);
    return;
  }

  const pick = target.closest<HTMLElement>('[data-pick]');
  if (pick) {
    store.addPick(JSON.parse(pick.dataset.pick as string) as ComposeItemInput);
    return;
  }

  const move = target.closest<HTMLElement>('[data-pick-move]');
  if (move) {
    store.movePick(Number(move.dataset.pickMove), Number(move.dataset.delta));
    return;
  }

  const remove = target.closest<HTMLElement>('[data-pick-remove]');
  if (remove) {
    store.removePickAt(Number(remove.dataset.pickRemove));
    return;
  }

  const action = target.closest<HTMLElement>('[data-action]');
  if (!action) return;
  switch (action.dataset.action) {
    case 'open-compose':
      store.openCompose();
      break;
    case 'close-compose':
      store.closeCompose();
      break;
    case 'toggle-mode':
      store.toggleMode();
      break;
    case 'submit-compose':
      void store.submitCompose();
      break;
  }
});

root.addEventListener('submit', (ev) => {
  const form = (ev.target as HTMLElement).closest<HTMLFormElement>('[data-adhoc]');
  if (!form) return;
  ev.preventDefault();
  const data = new FormData(form);
  const label = String(data.get('label') ?? '').trim();
  if (!label) return;
  const directionId = String(data.get('directionId') ?? '') || null;
  store.addPick({ label, sourceKind: 'adhoc', sourceRef: null, directionId });
  form.reset();
});

/* ------------------------------------------------------------------ clocks */

setInterval(() => {
  const el = root.querySelector<HTMLElement>('[data-elapsed]');
  const mode = store.state?.data.state.data?.mode;
  if (el && mode?.isOn && mode.since) {
    const secs = (Date.now() - Date.parse(mode.since)) / 1000;
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    el.textContent = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
}, 1000);

// Keep the small clock and "as of" marks honest between polls; never while the
// compose sheet is open (it would fight the typing).
setInterval(() => {
  if (!store.ui.composing) scheduleRender();
}, 20_000);

/* --------------------------------------------------------- focus stability */

interface FocusSnapshot {
  selector: string;
  start: number | null;
  end: number | null;
  value: string | null;
}

function captureFocus(): FocusSnapshot | null {
  const el = document.activeElement as HTMLInputElement | null;
  if (!el || !root.contains(el)) return null;
  const name = el.getAttribute('name');
  if (!name) return null;
  return {
    selector: `[name="${name}"]`,
    start: el.selectionStart ?? null,
    end: el.selectionEnd ?? null,
    value: el.value ?? null,
  };
}

function restoreFocus(snap: FocusSnapshot | null): void {
  if (!snap) return;
  const el = root.querySelector<HTMLInputElement>(snap.selector);
  if (!el) return;
  if (snap.value !== null) el.value = snap.value;
  el.focus();
  if (snap.start !== null && snap.end !== null && el.setSelectionRange) {
    try {
      el.setSelectionRange(snap.start, snap.end);
    } catch {
      /* selection is not supported on this input type */
    }
  }
}

/* ------------------------------------------------------------- dev harness */

if (import.meta.env.DEV) {
  void import('./dev-bar').then((m) => m.mountDevBar(store));
}

store.start();
render();
